/**
 * Gerador de PDFs por etapa do workflow.
 * Funções recebem os dados já carregados nos tabs.
 * As funções de FIV são async para buscar RGD dos animais.
 */
import { fetchAnimalsRgd } from '../data/animals';
import { previsaoDeParto } from '../constants/workflowGranular';
import {
  identidadeDoLaboratorio, assinaturaDoRodape, linhaDoResponsavel, CSS_LABORATORIO,
} from './laboratorioNoRelatorio';

// Tinta unica das fichas. Etapa se identifica pelo titulo, nao pela cor.
const INK = '#334155'; // slate-700

export const fmt = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('pt-BR') : '—';
export const n   = (v) => (v != null && v !== '') ? v : '—';

/** Retorna "Nome | RGD" ou só "Nome" */
function donorLabel(name, rgd) {
  if (!name && !rgd) return '—';
  if (!rgd) return n(name);
  return `${n(name)} <span style="color:#94a3b8;font-size:10px">| ${rgd}</span>`;
}

/** Busca RGD dos animais pelo donor_id. Retorna Map<donor_id, rgd> */
async function fetchRGDMap(donorIds) {
  const ids = donorIds.filter(Boolean);
  if (ids.length === 0) return {};
  const data = await fetchAnimalsRgd(ids);
  const map = {};
  (data || []).forEach(a => { map[a.id] = a.rgd || null; });
  return map;
}

// Exportado porque a ficha de ida da TE imprime a mesma fase do embriao:
// duas tabelas de rotulo para o mesmo vocabulario viram divergencia silenciosa.
export const STAGE_LABELS = {
  'BN': 'BN', 'BI': 'BI', 'BL': 'BL', 'BX': 'BX', 'BX-ECLO': 'BX-ECLO',
  'Bi': 'BI', 'Bl': 'BL', 'Bx': 'BX', 'Be': 'BX-ECLO',
  'morula': 'Mórula', 'blastocyst': 'BL', 'expanded': 'BX',
  'hatching': 'BX-ECLO', 'hatched': 'Ecl.', 'early': 'Inicial'
};

const CL_LABELS = { 1: 'G1', 2: 'G2', 3: 'G3', 4: 'G4' };

export function serviceHeader(service) {
  const label = service.service_label ? ` · ${service.service_label}` : '';
  return `
    <div class="page-header">
      ${identidadeDoLaboratorio()}
      <div class="header-right">
        <div class="client">${service.client_name}${label}</div>
        <div class="meta">Cód: ${service.service_code || '—'} &nbsp;|&nbsp; ${fmt(service.date)}</div>
      </div>
    </div>`;
}

export function baseCSS(accentColor = INK) {
  return `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #1a1a1a; padding: 20px; }
    .page-header { display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid ${accentColor}; padding-bottom: 10px; margin-bottom: 14px; }
    .logo { height: 42px; object-fit: contain; }
    .header-right { text-align: right; }
    .client { font-size: 15px; font-weight: 700; color: #111; }
    .meta { font-size: 11px; color: #64748b; margin-top: 2px; }
    .section-title { font-size: 13px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
      background: ${accentColor}; color: white; padding: 5px 10px; border-radius: 4px 4px 0 0; }
    .session-info { display: flex; flex-wrap: wrap; gap: 4px 20px; background: #f8fafc;
      border: 1px solid #e2e8f0; border-top: none; padding: 6px 10px; font-size: 11px; margin-bottom: 10px; }
    .session-info b { color: #334155; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; border: 1px solid #e2e8f0; border-radius: 0 0 4px 4px; overflow: hidden; }
    th { background: #f1f5f9; text-align: left; padding: 5px 8px; font-size: 10px; font-weight: 700;
      text-transform: uppercase; color: #334155; border-bottom: 1px solid #cbd5e1; white-space: nowrap; }
    td { padding: 4px 8px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
    tr:last-child td { border-bottom: none; }
    tfoot td { background: #f8fafc; border-top: 2px solid #cbd5e1; font-weight: 600; }
    .ok  { font-weight: 600; }
    .no  { color: #64748b; }
    .dim { color: #94a3b8; }
    .num { font-weight: 700; color: ${accentColor}; }
    .footer { margin-top: 16px; border-top: 1px solid #e2e8f0; padding-top: 6px; font-size: 10px; color: #94a3b8; text-align: center; }
    @page { size: A4; margin: 14mm; }
    /* Lista de 30 receptoras passa da folha: o cabecalho tem que repetir, e
       linha nem bloco de assinatura podem ser cortados ao meio. */
    thead { display: table-header-group; }
    tr, .bloco { page-break-inside: avoid; }
    @media print { body { padding: 10px; } }
    ${CSS_LABORATORIO}`;
}

export function openPrint(title, html) {
  const win = abrirJanelaDeImpressao();
  if (win) escreverImpressao(win, title, html);
}

/**
 * A janela, aberta ANTES de ter o que imprimir.
 *
 * Relatório que precisa buscar dados abre a janela no clique e escreve depois:
 * `window.open` chamado depois de um `await` já não conta como gesto do
 * usuário, e o navegador o trata como pop-up e bloqueia.
 */
export function abrirJanelaDeImpressao() {
  const win = window.open('', '_blank', 'width=900,height=720');
  if (!win) { alert('Permita pop-ups para gerar o PDF.'); return null; }
  win.document.write('<p style="font-family:Arial,sans-serif;color:#64748b;padding:20px">Montando o relatório…</p>');
  return win;
}

export function escreverImpressao(win, title, html) {
  win.document.open();
  win.document.write(`<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"><title>${title}</title></head><body>${html}</body></html>`);
  win.document.close();
  win.onload = () => { win.focus(); win.print(); };
}

// ─── OPU ─────────────────────────────────────────────────────────────────────

export function generateOPUPDF(service, opuData) {
  if (!opuData) { alert('Nenhum dado de OPU para exportar.'); return; }

  const { session = {}, donors = [] } = opuData;

  const rows = donors.length === 0
    ? `<tr><td colspan="8" style="text-align:center;color:#94a3b8;padding:12px;font-style:italic">Nenhuma doadora registrada</td></tr>`
    : donors.map((d, i) => {
        const viav = parseInt(d.oocytes_viable) || 0;
        const total = parseInt(d.oocytes_recovered) || 0;
        const taxa = total > 0 ? `${Math.round((viav / total) * 100)}%` : '—';
        return `<tr>
          <td class="num">${i + 1}</td>
          <td>${n(d.animal_identifier)}</td>
          <td>${n(d.breed)}</td>
          <td>${n(d.bull)}</td>
          <td>${n(d.aspiration_time)}</td>
          <td style="text-align:center">${viav}</td>
          <td style="text-align:center;font-weight:700">${total}</td>
          <td style="text-align:center" class="${taxa !== '—' ? 'ok' : 'dim'}">${taxa}</td>
          ${d.observations ? `<td style="font-style:italic;color:#64748b">${d.observations}</td>` : '<td class="dim">—</td>'}
        </tr>`;
      }).join('');

  const totalViav  = donors.reduce((s, d) => s + (parseInt(d.oocytes_viable) || 0), 0);
  const totalTotal = donors.reduce((s, d) => s + (parseInt(d.oocytes_recovered) || 0), 0);

  const html = `<style>${baseCSS()}</style>
  ${serviceHeader(service)}
  <div class="section-title">OPU — Coleta de Oócitos</div>
  <div class="session-info">
    <span><b>Data:</b> ${fmt(session.date)}</span>
    <span><b>Horário:</b> ${n(session.time)}</span>
    <span><b>Fazenda:</b> ${n(session.farm)}</span>
    <span><b>Protocolo:</b> ${n(session.protocol)}</span>
    ${opuData.general_observations ? `<span><b>Obs gerais:</b> ${opuData.general_observations}</span>` : ''}
  </div>
  <table>
    <thead><tr>
      <th>#</th><th>Doadora</th><th>Raça</th><th>Touro</th>
      <th>Hora Asp.</th><th style="text-align:center">Viáveis</th>
      <th style="text-align:center">Total</th><th style="text-align:center">Taxa%</th><th>Obs</th>
    </tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr>
      <td colspan="5" style="text-align:right">Totais:</td>
      <td style="text-align:center">${totalViav}</td>
      <td style="text-align:center;font-size:13px">${totalTotal}</td>
      <td colspan="2"></td>
    </tr></tfoot>
  </table>
  <div class="footer">${assinaturaDoRodape()} · OPU · ${service.client_name}${linhaDoResponsavel()}</div>`;

  openPrint(`OPU – ${service.client_name}`, html);
}

// ─── FIV Clivagem ─────────────────────────────────────────────────────────────

export async function generateFIVClivagemPDF(service, fivData) {
  if (!fivData?.donors_processing) { alert('Nenhum dado de clivagem para exportar.'); return; }

  const { session = {}, donors_processing: donors = [] } = fivData;

  const rgdMap = await fetchRGDMap(donors.map(d => d.donor_id));

  const rows = donors.map((d, i) => {
    const viav = parseInt(d.viable_oocytes) || 0;
    const cliv = parseInt(d.cleaved_oocytes) || 0;
    const taxa = viav > 0 && cliv > 0 ? `${Math.round((cliv / viav) * 100)}%` : '—';
    const rgd  = rgdMap[d.donor_id] || d.rgd || null;
    return `<tr>
      <td class="num">${i + 1}</td>
      <td>${donorLabel(d.donor_identifier, rgd)}</td>
      <td>${n(d.bull)}</td>
      <td style="text-align:center">${viav}</td>
      <td style="text-align:center;font-weight:700">${cliv}</td>
      <td style="text-align:center" class="${taxa !== '—' ? 'ok' : 'dim'}">${taxa}</td>
    </tr>`;
  }).join('') || `<tr><td colspan="6" style="text-align:center;color:#94a3b8;padding:12px;font-style:italic">Nenhuma doadora</td></tr>`;

  const totalViav = donors.reduce((s, d) => s + (parseInt(d.viable_oocytes) || 0), 0);
  const totalCliv = donors.reduce((s, d) => s + (parseInt(d.cleaved_oocytes) || 0), 0);
  const taxaGeral = totalViav > 0 && totalCliv > 0 ? `${Math.round((totalCliv / totalViav) * 100)}%` : '—';

  const html = `<style>${baseCSS()}</style>
  ${serviceHeader(service)}
  <div class="section-title">FIV — Clivagem D2</div>
  <div class="session-info">
    <span><b>Data recepção:</b> ${fmt(session.reception_date)}</span>
    <span><b>Embriologista:</b> ${n(session.embryologist_name)}</span>
  </div>
  <table>
    <thead><tr>
      <th>#</th><th>Doadora</th><th>Touro</th>
      <th style="text-align:center">Viáveis</th>
      <th style="text-align:center">Clivados</th>
      <th style="text-align:center">Taxa%</th>
    </tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr>
      <td colspan="3" style="text-align:right">Totais:</td>
      <td style="text-align:center">${totalViav}</td>
      <td style="text-align:center;font-size:13px">${totalCliv}</td>
      <td style="text-align:center" class="ok">${taxaGeral}</td>
    </tr></tfoot>
  </table>
  <div class="footer">${assinaturaDoRodape()} · Clivagem D2 · ${service.client_name}${linhaDoResponsavel()}</div>`;

  openPrint(`Clivagem D2 – ${service.client_name}`, html);
}

// ─── FIV Embriões ─────────────────────────────────────────────────────────────

export async function generateFIVEmbryosPDF(service, fivData) {
  if (!fivData?.donors_processing) { alert('Nenhum dado de embriões para exportar.'); return; }

  const { session = {}, donors_processing: donors = [] } = fivData;

  const FIV_STAGES = [
    { key: 'stage_bi', label: 'BI' },
    { key: 'stage_bl', label: 'BL' },
    { key: 'stage_bx', label: 'BX' },
    { key: 'stage_bn', label: 'BN' },
    { key: 'stage_be', label: 'BE' },
  ];

  // Os estágios são o que vai para a TE; congelado e descarte se SOMAM a eles.
  // A tela manda `dest_*` já calculados por `comDestinos`, então aqui é só
  // somar — nunca deduzir o descarte por subtração, como se fazia antes.
  const getDonorTE    = (d) => FIV_STAGES.reduce((s, f) => s + (parseInt(d[f.key]) || 0), 0);
  const getDonorTotal = (d) =>
    getDonorTE(d) + (parseInt(d.dest_dt) || 0)
    + (parseInt(d.dest_vitri) || 0) + (parseInt(d.dest_desc) || 0);

  const rgdMap = await fetchRGDMap(donors.map(d => d.donor_id));

  const donorBlocks = donors.map((d, i) => {
    const total = getDonorTotal(d);
    if (total === 0) return '';

    const rgd = rgdMap[d.donor_id] || d.rgd || null;
    const donorTitle = rgd
      ? `${n(d.donor_identifier)} <span style="font-weight:400;color:#7c3aed">| ${rgd}</span>`
      : n(d.donor_identifier);

    const stageCells = FIV_STAGES.map(f => {
      const cnt = parseInt(d[f.key]) || 0;
      return `<td style="text-align:center;${cnt > 0 ? 'font-weight:700;color:#5b21b6' : 'color:#cbd5e1'}">${cnt || '—'}</td>`;
    }).join('');

    // TE vem dos estágios, não de `dest_fresh`: a coluna do banco é espelho e
    // pode estar velha em lançamento antigo. Descarte é AFIRMADO, não a sobra.
    const destFresh = getDonorTE(d);
    const destDT    = parseInt(d.dest_dt)    || 0;
    const destVitri = parseInt(d.dest_vitri) || 0;
    const descarte  = parseInt(d.dest_desc)  || 0;

    const destRow = `
      <tr style="background:#f8fafc">
        <td colspan="5" style="padding:4px 8px;color:#64748b;font-size:10px">
          Destino:
          ${destFresh  > 0 ? `<span style="color:#059669;font-weight:600"> TE ${destFresh}</span>` : ''}
          ${destDT     > 0 ? `<span style="color:#2563eb;font-weight:600"> · DT ${destDT}${d.dest_dt_obs ? ` (${d.dest_dt_obs})` : ''}</span>` : ''}
          ${destVitri  > 0 ? `<span style="color:#4f46e5;font-weight:600"> · Vitri ${destVitri}${d.dest_vitri_obs ? ` (${d.dest_vitri_obs})` : ''}</span>` : ''}
          ${descarte   > 0 ? `<span style="color:#dc2626;font-weight:600"> · Descarte ${descarte}</span>` : ''}
        </td>
      </tr>`;

    return `
      <div style="margin-bottom:14px">
        <div style="background:#f5f3ff;border:1px solid #ddd6fe;border-bottom:none;padding:5px 10px;font-weight:700;font-size:11px;color:#5b21b6;border-radius:4px 4px 0 0">
          ${i + 1}. ${donorTitle}${d.bull ? ` × <span style="font-weight:400">${d.bull}</span>` : ''}
          <span style="font-weight:400;color:#94a3b8;margin-left:8px">${total} embrião(ões)</span>
        </div>
        <table style="border-radius:0 0 4px 4px">
          <thead><tr>
            <th style="text-align:center">BI</th>
            <th style="text-align:center">BL</th>
            <th style="text-align:center">BX</th>
            <th style="text-align:center">BN</th>
            <th style="text-align:center">BE</th>
          </tr></thead>
          <tbody>
            <tr>${stageCells}</tr>
            ${destRow}
          </tbody>
        </table>
      </div>`;
  }).join('');

  const totalEmbryos = donors.reduce((s, d) => s + getDonorTotal(d), 0);
  const freshCount   = donors.reduce((s, d) => s + getDonorTE(d), 0);
  const frozenCount  = donors.reduce((s, d) => s + (parseInt(d.dest_dt) || 0) + (parseInt(d.dest_vitri) || 0), 0);

  const html = `<style>${baseCSS()}</style>
  ${serviceHeader(service)}
  <div class="section-title">FIV — Embriões D7</div>
  <div class="session-info">
    <span><b>Data recepção:</b> ${fmt(session.reception_date)}</span>
    <span><b>Embriologista:</b> ${n(session.embryologist_name)}</span>
    <span><b>Total de embriões:</b> <strong>${totalEmbryos}</strong></span>
    <span><b>TE:</b> ${freshCount} &nbsp;|&nbsp; <b>Congelados:</b> ${frozenCount}</span>
  </div>
  ${donorBlocks || '<p style="color:#94a3b8;font-style:italic;padding:12px">Nenhum embrião registrado.</p>'}
  <div class="footer">${assinaturaDoRodape()} · Embriões D7 · ${service.client_name}${linhaDoResponsavel()}</div>`;

  openPrint(`Embriões D7 – ${service.client_name}`, html);
}

// ─── TE ───────────────────────────────────────────────────────────────────────

export function generateTEPDF(service, session, availableEmbryos, transfersMap) {
  if (!availableEmbryos?.length) { alert('Nenhuma transferência para exportar.'); return; }


  const rows = availableEmbryos.map((embryo, i) => {
    const t = transfersMap[embryo.id] || {};
    const isApproved = t.status === 'APROVADA';
    const hasCL = t.pre_evaluation?.corpus_luteum;
    const clGrau = hasCL ? (CL_LABELS[t.pre_evaluation?.cl_quality] || '—') : '—';
    const dg30 = t.dates?.expected_dg30
      ? `<span style="color:#1d4ed8">${fmt(t.dates.expected_dg30)}</span>`
      : '<span class="dim">—</span>';

    const donorName = n(embryo.donor?.identifier);
    const donorRGD  = embryo.donor?.rgd || null;
    const donorCell = donorRGD
      ? `${donorName} <span style="color:#7c3aed;font-size:10px">| ${donorRGD}</span>`
      : donorName;

    return `<tr class="${isApproved ? 'approved' : ''}">
      <td class="num">${embryo.service_number != null ? `#${embryo.service_number}` : '<span class="dim">—</span>'}</td>
      <td>${donorCell}<br><span style="color:#94a3b8;font-size:10px">${n(embryo.bull_name)}</span></td>
      <td>${STAGE_LABELS[embryo.stage] || n(embryo.stage)}</td>
      <td>${n(t.recipient?.identifier)}</td>
      <td style="text-align:center">${hasCL ? '✓' : '<span class="dim">—</span>'}</td>
      <td style="text-align:center">${clGrau}</td>
      <td>${dg30}</td>
      <td>${isApproved && session?.date ? fmt(previsaoDeParto(session.date)) : '<span class="dim">—</span>'}</td>
      <td style="font-style:italic;color:#64748b">${t.observations || '—'}</td>
    </tr>`;
  }).join('');

  const approved = availableEmbryos.filter(e => transfersMap[e.id]?.status === 'APROVADA').length;

  const extraCSS = `
    tr.approved td { background: #f0fdf4; }`;

  const html = `<style>${baseCSS()}${extraCSS}</style>
  ${serviceHeader(service)}
  <div class="section-title">TE — Transferência de Embriões</div>
  <div class="session-info">
    <span><b>Data:</b> ${fmt(session?.date)}</span>
    <span><b>Fazenda:</b> ${n(session?.farm)}</span>
    <span><b>Cidade:</b> ${n(session?.city)}</span>
    <span><b>Técnico:</b> ${n(session?.technician_name)}</span>
    <span><b>Transferidos:</b> <strong>${approved}</strong></span>
  </div>
  <table>
    <thead><tr>
      <th>Emb.</th><th>Doadora × Touro</th><th>Estágio</th><th>Receptora</th>
      <th style="text-align:center">CL</th><th style="text-align:center">Grau</th>
      <th>DG30 Prev.</th><th>Parto prev.</th><th>Obs</th>
    </tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr>
      <td colspan="6" style="text-align:right">Transferidos / Total:</td>
      <td colspan="3"><strong>${approved} / ${availableEmbryos.length}</strong></td>
    </tr></tfoot>
  </table>
  <div class="footer">${assinaturaDoRodape()} · TE · ${service.client_name}${linhaDoResponsavel()}</div>`;

  openPrint(`TE – ${service.client_name}`, html);
}

// ─── DG30 / DG60 ──────────────────────────────────────────────────────────────

// Data do check, venha ela como `2026-09-20` ou como timestamp inteiro.
const dia = (d) => (d ? fmt(String(d).slice(0, 10)) : '—');

const ROTULO_DO_RESULTADO = {
  PRENHE: 'Prenhe', VAZIA: 'Vazia', INCONCLUSIVO: 'Inconclusivo', ABORTOU: 'Abortou',
};
const resultado = (r) => ROTULO_DO_RESULTADO[r] || '<span class="dim">Pendente</span>';
const taxa = (parte, todo) => (todo > 0 ? ` (${Math.round((parte / todo) * 100)}%)` : '');

/**
 * Quantos embriões a TE transferiu. O laboratório pediu (29/10/2026) que o
 * resultado das prenhezes traga as transferidas ao lado das prenhes: o DG60 só
 * lista as que estavam prenhes no DG30, e "8 / 9" ali não diz que eram 21
 * transferidas. Sem TE lançada, nulo — e a linha não aparece.
 */
const transferidasDaTE = (service) => {
  const q = Number(service?.te_quantity);
  return service?.te_done && q > 0 ? q : null;
};

const sessaoDoDG = (session, total, transferidas) => `
  <div class="session-info">
    <span><b>Data:</b> ${fmt(session?.date)}</span>
    <span><b>Técnico:</b> ${n(session?.technician_name)}</span>
    <span><b>Método:</b> ${n(session?.method)}</span>
    ${transferidas ? `<span><b>Transferidas:</b> ${transferidas}</span>` : ''}
    <span><b>Receptoras no DG:</b> ${total}</span>
  </div>`;

export function generateDG30PDF(service, dg30) {
  const checks = dg30?.checks || [];
  if (!checks.length) { alert('Nenhuma receptora no DG30 para exportar.'); return; }

  const rows = checks.map(c => `<tr>
      <td>${n(c.recipient_identifier)}</td>
      <td class="num" style="text-align:center">${c.embryo_number != null ? `#${c.embryo_number}` : '—'}</td>
      <td>${dia(c.expected_date)}</td>
      <td class="${c.result === 'PRENHE' ? 'ok' : ''}">${resultado(c.result)}</td>
      <td>${c.result === 'PRENHE' ? fmt(previsaoDeParto(c.te_date)) : '—'}</td>
      <td>${n(c.viability)}</td>
      <td>${n(c.heartbeat)}</td>
      <td style="text-align:center">${n(c.size_mm)}</td>
      <td>${n(c.empty_cause || c.inconclusive_reason)}${c.reschedule_date ? ` · reag. ${dia(c.reschedule_date)}` : ''}</td>
      <td style="font-style:italic;color:#64748b">${n(c.observations)}</td>
    </tr>`).join('');

  const conta = (r) => checks.filter(c => c.result === r).length;
  const prenhes = conta('PRENHE');
  const transferidas = transferidasDaTE(service);

  const html = `<style>${baseCSS()}</style>
  ${serviceHeader(service)}
  <div class="section-title">DG 30 dias — Diagnóstico de gestação</div>
  ${sessaoDoDG(dg30.session, checks.length, transferidas)}
  <table>
    <thead><tr>
      <th>Receptora</th><th style="text-align:center">Emb.</th><th>DG prev.</th>
      <th>Resultado</th><th>Parto prev.</th><th>Viabilidade</th><th>Batimentos</th>
      <th style="text-align:center">Tam. mm</th><th>Causa / motivo</th><th>Obs</th>
    </tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr>
      <td colspan="10">
        ${transferidas ? `Transferidas: <strong>${transferidas}</strong> &nbsp;·&nbsp; ` : ''}
        Prenhes: <strong>${prenhes} / ${transferidas || checks.length}${taxa(prenhes, transferidas || checks.length)}</strong>
        &nbsp;·&nbsp; Vazias: ${conta('VAZIA')}
        &nbsp;·&nbsp; Inconclusivas: ${conta('INCONCLUSIVO')}
        &nbsp;·&nbsp; Pendentes: ${checks.filter(c => !c.result).length}
      </td>
    </tr></tfoot>
  </table>
  <div class="footer">${assinaturaDoRodape()} · DG30 · ${service.client_name}${linhaDoResponsavel()}</div>`;

  openPrint(`DG30 – ${service.client_name}`, html);
}

export function generateDG60PDF(service, dg60) {
  const checks = dg60?.checks || [];
  if (!checks.length) { alert('Nenhuma receptora no DG60 para exportar.'); return; }

  const sexo = (s) => (s?.sex
    ? `${s.sex}${s.confidence ? ` <span class="dim">(${s.confidence})</span>` : ''}`
    : '—');

  const rows = checks.map(c => `<tr>
      <td>${n(c.recipient_identifier)}</td>
      <td class="num" style="text-align:center">${c.embryo_number != null ? `#${c.embryo_number}` : '—'}</td>
      <td class="${c.result === 'PRENHE' ? 'ok' : ''}">${resultado(c.result)}</td>
      <td>${c.result === 'PRENHE' ? fmt(previsaoDeParto(c.te_date)) : '—'}</td>
      <td>${n(c.viability)}</td>
      <td>${n(c.heartbeat)}</td>
      <td style="text-align:center">${n(c.size_mm)}</td>
      <td>${sexo(c.sexing)}</td>
      <td style="font-style:italic;color:#64748b">${n(c.observations)}</td>
    </tr>`).join('');

  const prenhes = checks.filter(c => c.result === 'PRENHE').length;
  const transferidas = transferidasDaTE(service);

  const html = `<style>${baseCSS()}</style>
  ${serviceHeader(service)}
  <div class="section-title">DG 60 dias — Confirmação de prenhez</div>
  ${sessaoDoDG(dg60.session, checks.length, transferidas)}
  <table>
    <thead><tr>
      <th>Receptora</th><th style="text-align:center">Emb.</th><th>Resultado</th><th>Parto prev.</th>
      <th>Viabilidade</th><th>Batimentos</th><th style="text-align:center">Tam. mm</th>
      <th>Sexo</th><th>Obs</th>
    </tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr>
      <td colspan="9">
        ${transferidas ? `Transferidas: <strong>${transferidas}</strong> &nbsp;·&nbsp; ` : ''}
        Prenhezes confirmadas: <strong>${prenhes} / ${transferidas || checks.length}${taxa(prenhes, transferidas || checks.length)}</strong>
        ${transferidas ? `&nbsp;·&nbsp; ${prenhes} de ${checks.length} prenhes no DG30` : ''}
        &nbsp;·&nbsp; Abortos: ${checks.filter(c => c.result === 'ABORTOU').length}
        &nbsp;·&nbsp; Pendentes: ${checks.filter(c => !c.result).length}
      </td>
    </tr></tfoot>
  </table>
  <div class="footer">${assinaturaDoRodape()} · DG60 · ${service.client_name}${linhaDoResponsavel()}</div>`;

  openPrint(`DG60 – ${service.client_name}`, html);
}
